import { format, isToday, isYesterday } from 'date-fns';
import moment from 'moment';

import Notification from '../components/Notification/Notification';
import {
  FACEBOOK_SHARE_URL,
  LINKEDIN_SHARE_URL,
  SLACK_BASE_URL,
  TWITTER_SHARE_URL,
  WHATSAPP_SHARE_URL,
} from '../constants/commonConstants';
import { currenciesSymbolHash, DEFAULT_CURRENCY_SYMBOL } from '../constants/currenciesConstants';
import i18n from '../i18n';
import { uploadImage, uploadImportImage } from '../services/orgImageService';

const regexURL = /(https?:\/\/)?([\da-z\\.-]+)\.([a-z]{2,6})([\\/\w\\.-]*)*\/?/;
const linkedinRegex = /(https?:\/\/(www.)|(www.))?linkedin.com\/(mwlite\/|m\/)?in\/[a-zA-Z0-9_.-]+\/?/;
export const range = (start, end) =>
  Array(end - start + 1)
    .fill()
    .map((_, idx) => start + idx);

export const validateWebsiteLinks = (links) => {
  const websitesLinks = links.filter(({ type }) => type !== 'ln');
  const validateArray = websitesLinks.map((el) => regexURL.test(el.value));
  return !validateArray.includes(false);
};

export const mapDropdownValues = (dropdownArray) =>
  dropdownArray?.map((item) => ({
    label: item.name,
    value: item.name,
  }));

export const handleLogoUpload = async (fileObj, preview) => {
  let logoImageId = null;
  if (fileObj.file) {
    const files = [fileObj.file];
    const types = [fileObj.selectPhotoFor];
    logoImageId = await uploadImage(files, types);
  } else if (preview) {
    logoImageId = await uploadImportImage(preview);
  }
  return logoImageId?.data?.payload?.image[0]?.id || null;
};

export const findSelectedItem = (items, value) => items.find((item) => item.name === value) || null;

export const getFacebookShareLink = (jobUrl) =>
  `${FACEBOOK_SHARE_URL}u=${encodeURIComponent(jobUrl)}&amp;src=sdkpreparse`;

export const getLinkedInShareLink = (jobUrl) => `${LINKEDIN_SHARE_URL}&url=${encodeURIComponent(jobUrl)}`;

export const getTwitterShareLink = (jobUrl, text) =>
  `${TWITTER_SHARE_URL}?text=${encodeURIComponent(text)}&url=${encodeURIComponent(jobUrl)}`;

export const getWhatsAppShareLink = (jobUrl) => `${WHATSAPP_SHARE_URL}text=${encodeURIComponent(jobUrl)}`;

export const validateLinkedinLink = (links) => {
  const linkedinLink = links.find(({ type }) => type === 'ln');
  if (linkedinLink.value && !linkedinRegex.test(linkedinLink.value)) {
    return false;
  }

  return true;
};

export const validateWebsiteURL = (value) => regexURL.test(value);

export const notificationDate = (createdAt) => {
  const timeStr = format(createdAt, 'h:mm a');
  if (isToday(createdAt)) {
    return i18n.t('common:helpers.todayAt', { time: timeStr });
  }
  if (isYesterday(createdAt)) {
    return i18n.t('common:helpers.yesterdayAt', { time: timeStr });
  }

  return i18n.t('common:helpers.dateAt', { date: format(createdAt, 'MMMM d'), time: timeStr });
};

export const createYearList = (limit = 30, year = new Date().getFullYear()) => {
  const arr = [];
  for (let i = 0; i < limit; i += +1) {
    arr.push({ value: year - i, label: year - i });
  }
  return arr;
};

export const sortYear = (year1, year2) => {
  let result = 0;
  if (year1 < year2) {
    result = 1;
  } else if (year1 > year2) {
    result = -1;
  }
  return result;
};

export const slackAuthorizationUrl = () => {
  const query = new URLSearchParams();
  query.append('client_id', process.env.REACT_APP_SLACK_CLIENT_ID);
  query.append('scope', 'channels:read,chat:write,team:read,im:history,commands,chat:write.public,groups:read');
  query.append('user_scope', 'channels:read,groups:read,groups:write,channels:write');
  query.append('redirect_uri', `${window.location.origin}/slack/redirect`);

  window.open(`${SLACK_BASE_URL}?${query.toString()}`, '_self');
};

export const filterData = (data) =>
  data.filter((val) => {
    const { _destroy } = val;
    if (!_destroy) {
      return val;
    }
  });

export const areEqual = (array1, array2) => {
  if (array1.length === array2.length) {
    return array1.every((element) => {
      if (array2.includes(element)) {
        return true;
      }
      return false;
    });
  }
  return false;
};

export const locationObjToStr = (location) => {
  const locArray = [];

  if (!location) {
    return '';
  }

  if (location.full_address) {
    return location.full_address.trim();
  }

  if (location.city && location.city.trim()) {
    locArray.push(location.city.trim());
  }
  if (location.state && location.state.trim()) {
    locArray.push(location.state.trim());
  }
  if (location.country_name && location.country_name.trim()) {
    locArray.push(location.country_name.trim());
  } else if (location.country && location.country.trim()) {
    locArray.push(location.country.trim());
  }

  return locArray.join(', ');
};

export const isDateValid = (date) => {
  const dateMoment = moment(date);
  if (dateMoment.isValid()) {
    return true;
  }

  return false;
};

export const getDateTimeFromNow = (time) => {
  const timeVal = typeof time === 'number' ? moment.unix(time) : moment(time);
  const today = moment();
  const diffDays = today.diff(timeVal, 'days');
  const diffMonths = today.diff(timeVal, 'months');

  if (diffDays === 0) {
    return i18n.t('common:time.today');
  }
  if (diffDays === 1) {
    return i18n.t('common:time.yesterday');
  }
  if (diffDays <= 30) {
    return i18n.t('common:helpers.daysAgo', { count: diffDays });
  }
  if (diffMonths === 1) {
    return i18n.t('common:helpers.monthAgo');
  }
  if (diffMonths <= 6) {
    return i18n.t('common:helpers.monthsAgo', { count: diffMonths });
  }
  if (diffMonths > 6) {
    return i18n.t('common:helpers.sixPlusMonthsAgo');
  }
};

export const dateFormat = (date = '') => moment.unix(date).format('MMM DD');

export const copyToClipboard = (text, type) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      if (type === 'job') {
        Notification.success(i18n.t('common:helpers.jobLinkCopied'));
      } else {
        Notification.success(i18n.t('common:helpers.copiedToClipboard'));
      }
    })
    .catch(() => {
      Notification.error(i18n.t('common:helpers.copyFailed'));
    });
};

export const scrollElementToTop = (ref) => {
  ref.current.scrollIntoView({ behavior: 'auto' }, true);
};

export const strJoin = (...args) => [...args].filter((i) => i).join(' ');

export const openSupportWidget = () => {
  if (window.Pylon) {
    window.Pylon('show');
  }
};

export const numberWithCommas = (number) => number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const renderSalaryRange = (min_salary, max_salary, currency_id) => {
  const currencySymbol = currenciesSymbolHash[currency_id] || DEFAULT_CURRENCY_SYMBOL;

  if (min_salary > 0 && max_salary > 0) {
    return `${currencySymbol}${numberWithCommas(min_salary)} - ${currencySymbol}${numberWithCommas(max_salary)}`;
  }

  if (min_salary > 0) {
    return `${currencySymbol}${numberWithCommas(min_salary)}`;
  }

  if (max_salary > 0) {
    return `${currencySymbol}${numberWithCommas(max_salary)}`;
  }

  return null;
};

export const getOrdinalSuffix = (index) => {
  const j = index % 10;
  const k = index % 100;
  if (j === 1 && k !== 11) {
    return `${index}st`;
  }
  if (j === 2 && k !== 12) {
    return `${index}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${index}rd`;
  }
  return `${index}th`;
};

export const setLocationField = (values, location) => {
  const newValues = { ...values, current_location: location ? location.full_address : '' };
  if (location) {
    newValues.location = location;
  } else {
    delete newValues.location;
  }
  return newValues;
};

export const generateSkeletonKey = (section, index) => `${section}-skeleton-${index}`;

export const capitalizeWords = (string) => {
  if (!string) return '';

  return string
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatAmountWithCurrency = (amount, currency = 'USD') => {
  if (amount === 0) return '0';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatExchangeRate = (rate, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(rate);

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const normalizeURL = (url) => {
  if (!url) {
    return '';
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `https://${url}`;
};
